import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, updateProfile } from '../api/auth.api';
import { User } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import upazilas from '../data/bd-upazilas.json';

const divisions = [
  'Barisal',
  'Chattogram',
  'Dhaka',
  'Khulna',
  'Mymensingh',
  'Rajshahi',
  'Rangpur',
  'Sylhet',
];

const districts = [
  { name: 'Bagerhat', division: 'Khulna' },
  { name: 'Bandarban', division: 'Chattogram' },
  { name: 'Barguna', division: 'Barisal' },
  { name: 'Barisal', division: 'Barisal' },
  { name: 'Bhola', division: 'Barisal' },
  { name: 'Bogra', division: 'Rajshahi' },
  { name: 'Brahmanbaria', division: 'Chattogram' },
  { name: 'Chandpur', division: 'Chattogram' },
  { name: 'Chapai Nawabganj', division: 'Rajshahi' },
  { name: 'Chattogram', division: 'Chattogram' },
  { name: 'Chuadanga', division: 'Khulna' },
  { name: 'Comilla', division: 'Chattogram' },
  { name: "Cox's Bazar", division: 'Chattogram' },
  { name: 'Dhaka', division: 'Dhaka' },
  { name: 'Dinajpur', division: 'Rangpur' },
  { name: 'Faridpur', division: 'Dhaka' },
  { name: 'Feni', division: 'Chattogram' },
  { name: 'Gaibandha', division: 'Rangpur' },
  { name: 'Gazipur', division: 'Dhaka' },
  { name: 'Gopalganj', division: 'Dhaka' },
  { name: 'Habiganj', division: 'Sylhet' },
  { name: 'Jamalpur', division: 'Mymensingh' },
  { name: 'Jashore', division: 'Khulna' },
  { name: 'Jhalokati', division: 'Barisal' },
  { name: 'Jhenaidah', division: 'Khulna' },
  { name: 'Joypurhat', division: 'Rajshahi' },
  { name: 'Khagrachari', division: 'Chattogram' },
  { name: 'Khulna', division: 'Khulna' },
  { name: 'Kishoreganj', division: 'Dhaka' },
  { name: 'Kurigram', division: 'Rangpur' },
  { name: 'Kushtia', division: 'Khulna' },
  { name: 'Lakshmipur', division: 'Chattogram' },
  { name: 'Lalmonirhat', division: 'Rangpur' },
  { name: 'Madaripur', division: 'Dhaka' },
  { name: 'Magura', division: 'Khulna' },
  { name: 'Manikganj', division: 'Dhaka' },
  { name: 'Meherpur', division: 'Khulna' },
  { name: 'Moulvibazar', division: 'Sylhet' },
  { name: 'Munshiganj', division: 'Dhaka' },
  { name: 'Mymensingh', division: 'Mymensingh' },
  { name: 'Naogaon', division: 'Rajshahi' },
  { name: 'Narail', division: 'Khulna' },
  { name: 'Narayanganj', division: 'Dhaka' },
  { name: 'Narsingdi', division: 'Dhaka' },
  { name: 'Natore', division: 'Rajshahi' },
  { name: 'Netrokona', division: 'Mymensingh' },
  { name: 'Nilphamari', division: 'Rangpur' },
  { name: 'Noakhali', division: 'Chattogram' },
  { name: 'Pabna', division: 'Rajshahi' },
  { name: 'Panchagarh', division: 'Rangpur' },
  { name: 'Patuakhali', division: 'Barisal' },
  { name: 'Pirojpur', division: 'Barisal' },
  { name: 'Rajbari', division: 'Dhaka' },
  { name: 'Rajshahi', division: 'Rajshahi' },
  { name: 'Rangamati', division: 'Chattogram' },
  { name: 'Rangpur', division: 'Rangpur' },
  { name: 'Satkhira', division: 'Khulna' },
  { name: 'Shariatpur', division: 'Dhaka' },
  { name: 'Sherpur', division: 'Mymensingh' },
  { name: 'Sirajganj', division: 'Rajshahi' },
  { name: 'Sunamganj', division: 'Sylhet' },
  { name: 'Sylhet', division: 'Sylhet' },
  { name: 'Tangail', division: 'Dhaka' },
  { name: 'Thakurgaon', division: 'Rangpur' },
];

const roles = ['Batsman', 'Bowler', 'All-Rounder', 'Wicket-Keeper'];
const battingStyles = ['Right Hand', 'Left Hand'];
const bowlingStyles = [
  'Right Arm Fast',
  'Right Arm Medium',
  'Left Arm Fast',
  'Left Arm Spin',
  'Right Arm Spin',
];
const genderOptions = ['Male', 'Female', 'Other'];

type UpazilaEntry = {
  upazila: string;
  district: string;
  division: string;
};

const isEmail = (value: string) => /.+@.+\..+/.test(value);

const EditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    gender: '',
    fatherName: '',
    motherName: '',
    phone: '',
    whatsappNumber: '',
    email: '',
    dob: '',
    division: '',
    district: '',
    upazilla: '',
    playingRole: '',
    battingStyle: '',
    bowlingStyle: '',
    jerseySize: '',
    nid: '',
    cricheroes_profile_link: '',
  });
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const upazilaData = upazilas as UpazilaEntry[];

  const requiresBowlingStyle = useMemo(
    () => ['Bowler', 'All-Rounder'].includes(formData.playingRole),
    [formData.playingRole]
  );

  const availableDistricts = useMemo(() => {
    if (!formData.division) {
      return [];
    }
    return districts.filter((district) => district.division === formData.division);
  }, [formData.division]);

  const availableUpazilas = useMemo(() => {
    if (!formData.district) {
      return [];
    }
    const selectedDistrict = districts.find(d => d.name === formData.district);
    if (!selectedDistrict) {
      return [];
    }
    return upazilaData.filter((upazila) => upazila.district === selectedDistrict.name);
  }, [formData.district, upazilaData]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getMe();
        const userData = response.data;
        setUser(userData);
        
        // Format date of birth - handle both ISO string and date string formats
        let dobValue = '';
        if (userData.dob) {
          if (userData.dob.includes('T')) {
            dobValue = userData.dob.split('T')[0];
          } else {
            // If it's already in YYYY-MM-DD format
            dobValue = userData.dob;
          }
        }
        
        // Get division and district values
        const divisionValue = userData.division || '';
        const districtValue = userData.district_name || userData.district || userData.district_code || '';
        const upazillaValue = userData.upazilla_name || userData.upazilla || '';
        
        // Keep the district and upazilla values as-is - let the dropdown handle validation
        // The dropdown will show the value even if it's not in the filtered list
        setFormData({
          fullName: userData.full_name || userData.name || '',
          gender: userData.gender || '',
          fatherName: userData.father_name || '',
          motherName: userData.mother_name || '',
          phone: userData.phone || '',
          whatsappNumber: userData.whatsapp_number || userData.phone || '',
          email: userData.email || '',
          dob: dobValue,
          division: divisionValue,
          district: districtValue,
          upazilla: upazillaValue,
          playingRole: userData.playing_role || '',
          battingStyle: userData.batting_style || '',
          bowlingStyle: userData.bowling_style || '',
          jerseySize: userData.jersey_size || '',
          nid: userData.nid || '',
          cricheroes_profile_link: userData.cricheroes_profile_link || '',
        });
        
        // Log for debugging
        console.log('Loaded user data:', userData);
        console.log('Form data set:', {
          division: userData.division || '',
          district: userData.district || userData.district_name || '',
          upazilla: userData.upazilla || userData.upazilla_name || '',
        });
        
        // Set profile photo preview if available
        if (userData.profile_photo) {
          // If it's a URL string, use it directly
          if (typeof userData.profile_photo === 'string') {
            setPhotoPreview(userData.profile_photo);
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setErrors({ general: 'Failed to load profile data. Please try again.' });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const isValidURL = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDivisionChange = (value: string) => {
    // When division changes, check if current district is valid for new division
    const newDivisionDistricts = districts.filter((d) => d.division === value);
    const currentDistrictValid = newDivisionDistricts.some((d) => d.name === formData.district);
    
    setFormData((prev) => ({
      ...prev,
      division: value,
      // Only keep district if it's valid for the new division, otherwise clear it
      district: currentDistrictValid ? prev.district : '',
      upazilla: currentDistrictValid ? prev.upazilla : '',
    }));
  };

  const handleDistrictChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      district: value,
      upazilla: '',
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrors({ ...errors, profilePhoto: 'File size exceeds 5MB limit.' });
        e.target.value = '';
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, profilePhoto: 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP image.' });
        e.target.value = '';
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.profilePhoto;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required.';
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required.';
    }
    if (!formData.fatherName.trim()) {
      newErrors.fatherName = "Father's name is required.";
    }
    if (!formData.motherName.trim()) {
      newErrors.motherName = "Mother's name is required.";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    }
    if (!formData.whatsappNumber.trim()) {
      newErrors.whatsappNumber = 'WhatsApp number is required.';
    }
    if (formData.email && !isEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required.';
    }
    if (!formData.division) {
      newErrors.division = 'Division is required.';
    }
    if (!formData.district) {
      newErrors.district = 'District is required.';
    }
    if (!formData.upazilla.trim()) {
      newErrors.upazilla = 'Upazilla is required.';
    }
    if (!formData.playingRole) {
      newErrors.playingRole = 'Playing role is required.';
    }
    if (!formData.battingStyle) {
      newErrors.battingStyle = 'Batting style is required.';
    }
    if (requiresBowlingStyle && !formData.bowlingStyle) {
      newErrors.bowlingStyle = 'Bowling style is required.';
    }
    if (formData.cricheroes_profile_link && !isValidURL(formData.cricheroes_profile_link)) {
      newErrors.cricheroes_profile_link = 'Please enter a valid URL.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('full_name', formData.fullName);
      formDataToSend.append('gender', formData.gender);
      formDataToSend.append('father_name', formData.fatherName);
      formDataToSend.append('mother_name', formData.motherName);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('whatsapp_number', formData.whatsappNumber);
      if (formData.email) {
        formDataToSend.append('email', formData.email);
      }
      formDataToSend.append('dob', formData.dob);
      formDataToSend.append('division', formData.division);
      // Send district - send both district and district_name for backend compatibility
      formDataToSend.append('district', formData.district);
      formDataToSend.append('district_name', formData.district);
      // Send upazilla - send both upazilla and upazilla_name for backend compatibility
      formDataToSend.append('upazilla', formData.upazilla);
      formDataToSend.append('upazilla_name', formData.upazilla);
      formDataToSend.append('playing_role', formData.playingRole);
      formDataToSend.append('batting_style', formData.battingStyle);
      if (requiresBowlingStyle && formData.bowlingStyle) {
        formDataToSend.append('bowling_style', formData.bowlingStyle);
      }
      if (formData.jerseySize) {
        formDataToSend.append('jersey_size', formData.jerseySize);
      }
      if (formData.nid) {
        formDataToSend.append('nid', formData.nid);
      }
      if (formData.cricheroes_profile_link) {
        formDataToSend.append('cricheroes_profile_link', formData.cricheroes_profile_link);
      }
      if (profilePhoto) {
        formDataToSend.append('profile_photo', profilePhoto);
      }

      await updateProfile(formDataToSend);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setErrors({
          general: err.response?.data?.message || 'Invalid data. Please check your inputs.',
        });
      } else if (err.response?.status === 401) {
        setErrors({
          general: 'Your session has expired. Please log in again.',
        });
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      } else if (err.response?.status === 413) {
        setErrors({
          profilePhoto: 'File is too large. Maximum size is 5MB.',
        });
      } else {
        setErrors({
          general: err.response?.data?.message || 'Failed to update profile. Please try again.',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <h2 className="mb-3">Failed to load profile</h2>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card card-surface shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="h4 mb-0">Edit Profile</h1>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Back
                </button>
              </div>

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle me-2"></i>
                  Profile updated successfully! Redirecting to dashboard...
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccess(false)}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {errors.general && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.general}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      const newErrors = { ...errors };
                      delete newErrors.general;
                      setErrors(newErrors);
                    }}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="row g-3">
                {/* Profile Photo */}
                <div className="col-12">
                  <label className="form-label">Profile Photo</label>
                  <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                    {photoPreview && (
                      <div className="flex-shrink-0">
                        <img
                          src={photoPreview}
                          alt="Profile preview"
                          className="rounded-circle"
                          style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                        />
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <input
                        type="file"
                        className={`form-control ${errors.profilePhoto ? 'is-invalid' : ''}`}
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={saving}
                      />
                      {errors.profilePhoto && (
                        <div className="invalid-feedback">{errors.profilePhoto}</div>
                      )}
                      <div className="form-text">Max 5MB. Formats: JPEG, PNG, GIF, WEBP</div>
                    </div>
                  </div>
                </div>

                {/* Full Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    disabled={saving}
                  />
                  {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
                </div>

                {/* Gender */}
                <div className="col-md-6">
                  <label className="form-label">
                    Gender <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                    value={formData.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map((gender) => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </select>
                  {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                </div>

                {/* Father Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Father's Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.fatherName ? 'is-invalid' : ''}`}
                    value={formData.fatherName}
                    onChange={(e) => handleChange('fatherName', e.target.value)}
                    disabled={saving}
                  />
                  {errors.fatherName && <div className="invalid-feedback">{errors.fatherName}</div>}
                </div>

                {/* Mother Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Mother's Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.motherName ? 'is-invalid' : ''}`}
                    value={formData.motherName}
                    onChange={(e) => handleChange('motherName', e.target.value)}
                    disabled={saving}
                  />
                  {errors.motherName && <div className="invalid-feedback">{errors.motherName}</div>}
                </div>

                {/* Phone */}
                <div className="col-md-6">
                  <label className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    disabled={saving}
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>

                {/* WhatsApp Number */}
                <div className="col-md-6">
                  <label className="form-label">
                    WhatsApp Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.whatsappNumber ? 'is-invalid' : ''}`}
                    value={formData.whatsappNumber}
                    onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                    disabled={saving}
                  />
                  {errors.whatsappNumber && <div className="invalid-feedback">{errors.whatsappNumber}</div>}
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={saving}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Date of Birth */}
                <div className="col-md-6">
                  <label className="form-label">
                    Date of Birth <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                    value={formData.dob}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    disabled={saving}
                  />
                  {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                </div>

                {/* Division */}
                <div className="col-md-6">
                  <label className="form-label">
                    Division <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.division ? 'is-invalid' : ''}`}
                    value={formData.division}
                    onChange={(e) => handleDivisionChange(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select division</option>
                    {divisions.map((division) => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </select>
                  {errors.division && <div className="invalid-feedback">{errors.division}</div>}
                </div>

                {/* District */}
                <div className="col-md-6">
                  <label className="form-label">
                    District <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.district ? 'is-invalid' : ''}`}
                    value={formData.district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">
                      {formData.division ? 'Select district' : 'Select division first'}
                    </option>
                    {availableDistricts.map((district) => (
                      <option key={district.name} value={district.name}>
                        {district.name}
                      </option>
                    ))}
                    {/* Always show current district value if it exists but not in available list */}
                    {formData.district && !availableDistricts.find(d => d.name === formData.district) && (
                      <option value={formData.district}>
                        {formData.district}
                      </option>
                    )}
                  </select>
                  {errors.district && <div className="invalid-feedback">{errors.district}</div>}
                  {formData.district && !formData.division && (
                    <div className="form-text text-warning">
                      <i className="bi bi-info-circle me-1"></i>
                      Please select division to see all districts
                    </div>
                  )}
                </div>

                {/* Upazilla */}
                <div className="col-md-6">
                  <label className="form-label">
                    Upazilla <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.upazilla ? 'is-invalid' : ''}`}
                    value={formData.upazilla}
                    onChange={(e) => handleChange('upazilla', e.target.value)}
                    disabled={!formData.district || saving}
                  >
                    <option value="">
                      {formData.district ? 'Select upazilla' : 'Select district first'}
                    </option>
                    {availableUpazilas.map((upazila) => (
                      <option key={upazila.upazila} value={upazila.upazila}>
                        {upazila.upazila}
                      </option>
                    ))}
                    {/* Show current upazilla if it exists but not in available list (for existing data) */}
                    {formData.upazilla && formData.district && !availableUpazilas.find(u => u.upazila === formData.upazilla) && (
                      <option value={formData.upazilla}>
                        {formData.upazilla}
                      </option>
                    )}
                  </select>
                  {errors.upazilla && <div className="invalid-feedback">{errors.upazilla}</div>}
                  {formData.upazilla && !formData.district && (
                    <div className="form-text text-warning">
                      <i className="bi bi-info-circle me-1"></i>
                      Please select district to see all upazillas
                    </div>
                  )}
                </div>

                {/* Playing Role */}
                <div className="col-md-6">
                  <label className="form-label">
                    Playing Role <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.playingRole ? 'is-invalid' : ''}`}
                    value={formData.playingRole}
                    onChange={(e) => handleChange('playingRole', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select role</option>
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  {errors.playingRole && <div className="invalid-feedback">{errors.playingRole}</div>}
                </div>

                {/* Batting Style */}
                <div className="col-md-6">
                  <label className="form-label">
                    Batting Style <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.battingStyle ? 'is-invalid' : ''}`}
                    value={formData.battingStyle}
                    onChange={(e) => handleChange('battingStyle', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select style</option>
                    {battingStyles.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                  {errors.battingStyle && <div className="invalid-feedback">{errors.battingStyle}</div>}
                </div>

                {/* Bowling Style */}
                <div className="col-md-6">
                  <label className="form-label">
                    Bowling Style {requiresBowlingStyle && <span className="text-danger">*</span>}
                  </label>
                  <select
                    className={`form-select ${errors.bowlingStyle ? 'is-invalid' : ''}`}
                    value={formData.bowlingStyle}
                    onChange={(e) => handleChange('bowlingStyle', e.target.value)}
                    disabled={!requiresBowlingStyle || saving}
                  >
                    <option value="">
                      {requiresBowlingStyle ? 'Select style' : 'Select playing role first'}
                    </option>
                    {bowlingStyles.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                  {errors.bowlingStyle && <div className="invalid-feedback">{errors.bowlingStyle}</div>}
                </div>

                {/* Jersey Size */}
                <div className="col-md-6">
                  <label className="form-label">Jersey Size</label>
                  <select
                    className="form-select"
                    value={formData.jerseySize}
                    onChange={(e) => handleChange('jerseySize', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select jersey size (optional)</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                </div>

                {/* NID */}
                <div className="col-md-6">
                  <label className="form-label">NID (National ID)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.nid}
                    onChange={(e) => handleChange('nid', e.target.value)}
                    placeholder="Enter your NID number"
                    disabled={saving}
                  />
                </div>

                {/* Cricheroes Profile Link */}
                <div className="col-12">
                  <label className="form-label">Cricheroes Profile Link</label>
                  <input
                    type="url"
                    className={`form-control ${errors.cricheroes_profile_link ? 'is-invalid' : ''}`}
                    placeholder="https://cricheroes.in/..."
                    value={formData.cricheroes_profile_link}
                    onChange={(e) => handleChange('cricheroes_profile_link', e.target.value)}
                    disabled={saving}
                  />
                  {errors.cricheroes_profile_link && (
                    <div className="invalid-feedback">{errors.cricheroes_profile_link}</div>
                  )}
                  <div className="form-text">Enter your Cricheroes profile URL (optional)</div>
                </div>

                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/dashboard')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-arena flex-grow-1"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;
